import React, { memo, useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { CATEGORY_IMAGES, DEFAULT_IMAGE } from './constants';

/**
 * ListingCardImage
 * Handles image display with:
 * - Lazy loading (loads when entering viewport)
 * - Image carousel navigation
 * - Fallback images per category
 * - Loading shimmer state
 * - Smart back navigation via state
 * - Listing type badge overlay
 */
const ListingCardImage = memo(({
  listingId,
  images = [],
  title,
  category,
  compact = false,
  listingType,
  children // For overlay badges, buttons, etc.
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imageRef = useRef(null);
  
  // Get fallback image based on category
  const fallbackImage = CATEGORY_IMAGES[category] || DEFAULT_IMAGE;
  const imageList = images.length > 0 ? images : [fallbackImage];
  const hasMultipleImages = imageList.length > 1;
  
  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px', threshold: 0.1 }
    );
    
    if (imageRef.current) {
      observer.observe(imageRef.current);
    }
    
    return () => observer.disconnect();
  }, []);
  
  // Navigation handlers
  const handlePrev = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? imageList.length - 1 : prev - 1));
    setIsLoaded(false);
  }, [imageList.length]);
  
  const handleNext = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === imageList.length - 1 ? 0 : prev + 1));
    setIsLoaded(false);
  }, [imageList.length]);
  
  const handleImageLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);
  
  const handleImageError = useCallback((e) => {
    e.target.src = fallbackImage;
    setIsLoaded(true);
  }, [fallbackImage]);
  
  // Navigate to listing with return path state
  const handleNavigateToListing = useCallback((e) => {
    e.preventDefault();
    const returnState = {
      returnPath: location.pathname + location.search,
      scrollY: window.scrollY,
      fromMarketplace: location.pathname.includes('/marketplace') || location.pathname.includes('/categories')
    };
    navigate(`/listing/${listingId}`, { state: returnState });
  }, [navigate, location, listingId]);
  
  return (
    <div 
      ref={imageRef}
      className={`relative ${compact ? 'aspect-[4/3]' : 'aspect-[4/3]'} overflow-hidden bg-muted group`}
      data-testid={`listing-image-container-${listingId}`}
    >
      <div onClick={handleNavigateToListing} className="cursor-pointer h-full w-full">
        {/* Loading shimmer */}
        {(!isInView || !isLoaded) && (
          <div className="absolute inset-0 bg-muted">
            <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          </div>
        )}
        
        {/* Actual image - only render if in viewport */}
        {isInView && (
          <img
            src={imageList[currentIndex]}
            alt={title}
            className={`w-full h-full min-h-full min-w-full object-cover object-center transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={handleImageLoad}
            onError={handleImageError}
            loading="lazy"
            decoding="async"
          />
        )}
      </div>
      
      {/* Gradient overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none" />
      
      {/* Navigation arrows - only show on hover when multiple images */}
      {hasMultipleImages && (
        <>
          <button
            onClick={handlePrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10"
            data-testid={`listing-prev-image-${listingId}`}
            aria-label="Previous image"
          >
            <ChevronLeft className="w-5 h-5 text-gray-800" />
          </button>
          <button
            onClick={handleNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10"
            data-testid={`listing-next-image-${listingId}`}
            aria-label="Next image"
          >
            <ChevronRight className="w-5 h-5 text-gray-800" />
          </button>
          
          {/* Image count badge */}
          <div 
            className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/60 text-white text-[10px] font-medium rounded-full"
            data-testid={`listing-image-count-${listingId}`}
          >
            {currentIndex + 1}/{imageList.length}
          </div>
        </>
      )}
      
      {/* Slot for children (badges, ribbons, buttons) */}
      {children}
    </div>
  );
});

ListingCardImage.displayName = 'ListingCardImage';

export default ListingCardImage;
